import * as LucideIcons from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { useState } from "react";
import { useInput, useTranslate } from "ra-core";
import { X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

const CATEGORY_ICONS: string[] = [
  "Banknote",
  "Coins",
  "Wallet",
  "PiggyBank",
  "CreditCard",
  "HandCoins",
  "DollarSign",
  "CircleDollarSign",
  "ReceiptText",
  "Receipt",
  "TrendingUp",
  "TrendingDown",
  "ArrowDownLeft",
  "ArrowUpRight",
  "ShoppingBag",
  "ShoppingCart",
  "Briefcase",
  "Building",
  "Building2",
  "Landmark",
  "Server",
  "Cloud",
  "Laptop",
  "Smartphone",
  "Globe",
  "Users",
  "User",
  "Megaphone",
  "BarChart",
  "BarChart3",
  "Calculator",
  "FileText",
  "ClipboardList",
  "ScrollText",
  "Pencil",
  "Paperclip",
  "Printer",
  "Sparkles",
  "Brain",
  "Cpu",
  "Bot",
  "Mic",
  "Calendar",
  "Star",
  "Tag",
  "Package",
  "Box",
  "Truck",
  "CirclePlus",
  "CircleMinus",
  "Gift",
  "Heart",
];

const iconsAny = LucideIcons as unknown as Record<string, LucideIcon>;

const toPascalCase = (s: string) =>
  s
    .split(/[-_\s]+/)
    .filter(Boolean)
    .map((p) => p.charAt(0).toUpperCase() + p.slice(1))
    .join("");

export const getCategoryIcon = (name?: string | null): LucideIcon | null => {
  if (!name) return null;
  if (iconsAny[name]) return iconsAny[name];
  const pascal = toPascalCase(name);
  return iconsAny[pascal] ?? null;
};

interface Props {
  source: string;
  label?: string;
}

export const CategoryIconPicker = ({ source, label }: Props) => {
  const [open, setOpen] = useState(false);
  const { field } = useInput({ source });
  const translate = useTranslate();
  const CurrentIcon = getCategoryIcon(field.value);
  const labelText = label
    ? translate(label, { _: label })
    : translate("resources.transaction_categories.fields.icon", {
        _: "Иконка",
      });

  return (
    <div className="space-y-2">
      <Label>{labelText}</Label>
      <div className="flex items-center gap-2">
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button
              type="button"
              variant="outline"
              className="justify-start gap-2"
            >
              {CurrentIcon ? (
                <CurrentIcon className="h-4 w-4" />
              ) : (
                <span className="text-muted-foreground text-xs">—</span>
              )}
              <span className="text-sm text-muted-foreground">
                {field.value || translate("finance.iconPicker.pick", { _: "Выбрать" })}
              </span>
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-72 p-3" align="start">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium">
                {translate("finance.iconPicker.title", { _: "Иконка категории" })}
              </span>
              {field.value && (
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6"
                  onClick={() => {
                    field.onChange("");
                    setOpen(false);
                  }}
                >
                  <X className="h-3 w-3" />
                </Button>
              )}
            </div>
            <div className="grid grid-cols-7 gap-1 max-h-64 overflow-y-auto">
              {CATEGORY_ICONS.map((iconName) => {
                const Icon = iconsAny[iconName];
                if (!Icon) return null;
                const isSelected =
                  field.value === iconName ||
                  toPascalCase(field.value || "") === iconName;
                return (
                  <Button
                    key={iconName}
                    type="button"
                    variant={isSelected ? "default" : "ghost"}
                    size="icon"
                    className="h-9 w-9"
                    title={iconName}
                    onClick={() => {
                      field.onChange(iconName);
                      setOpen(false);
                    }}
                  >
                    <Icon className="h-4 w-4" />
                  </Button>
                );
              })}
            </div>
          </PopoverContent>
        </Popover>
      </div>
    </div>
  );
};
